import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { acquisitionApi } from '../utils/api';
import { trackEvent } from '../utils/observability';
import { registerLinkFromMiniScan } from '../utils/miniScanHandoff';
import { SITE_URL } from '../config/business';
import MiniScanConsumerResult from './MiniScanConsumerResult';
import MiniScanLiveQuestions from './MiniScanLiveQuestions';
import AiContentNotice from './AiContentNotice';

/**
 * MiniScanWidget — primul contact al unui prospect cu produsul.
 *
 * 2026-08-14 (pipeline v2, MINISCAN_SPEC v1): widgetul ruleaza scanul
 * "consumator" — clasificare LLM (orice categorie) + 9 conversatii ChatGPT +
 * judge — si randeaza rezultatul prin <MiniScanConsumerResult>, aceeasi
 * componenta ca pagina publica /scan/:scan_id. Ce s-a schimbat fata de v1:
 *  - Detectia de industrie si descriptorul de sector au DISPARUT din formular:
 *    clasificarea e dinamica (Prompt 0), deci nu mai cerem nimic userului.
 *  - Poarta de email e HIBRIDA (teaser + gate) si traieste in componenta de
 *    rezultat, nu aici: backendul serveste headline + citat blurat, emailul
 *    deblocheaza restul (`result_full`). Gating-ul e server-side (views).
 *  - Verdictul e de tip "carlig" ("concurentul X e recomandat inaintea ta"),
 *    pe chei stabile din backend, cu copy-ul integral in i18n.
 *
 * PRINCIPIUL din 2026-08-13 ramane in picioare: tot ce se afiseaza vine din
 * date reale — citatul e verificat verbatim in backend, agregatele sunt
 * renumarate determinist peste judge, un citat neverificabil nu se afiseaza.
 *
 * <MiniScanResult> si <ScanShareBox> raman exportate de aici: prima randeaza
 * payload-urile v1 deja salvate (linkurile /scan/<id> vechi nu au voie sa
 * moara), a doua e refolosita de ambele versiuni.
 */

const GOLD = 'var(--gold, #D4AF37)';
const PANEL = {
  background: 'var(--surface, #FFFFFF)',
  border: `2px solid ${GOLD}`,
  borderRadius: 16,
  padding: 'clamp(18px, 5vw, 32px)',
  maxWidth: 780,
  margin: '0 auto',
  boxShadow: '0 20px 60px rgba(212,175,55,0.15)',
};
const MONO = 'DM Mono, monospace';
const SERIF = 'Playfair Display, serif';
const BODY = 'Cormorant Garamond, serif';

// Culorile urmeaza tonul intors de backend. Rosul e rezervat exclusiv erorilor
// tehnice — un brand care nu apare inca in AI nu e o eroare, e un punct de start.
const TONE_COLOR = {
  absent: '#6B7280',
  low: '#B7791F',
  mid: '#2C5282',
  high: '#2D6A4F',
  neutral: '#6B7280',
};

// `variant="hero"` — widgetul e elementul central al homepage-ului, sub H1-ul
// paginii. Isi ascunde atunci eyebrow-ul si titlul propriu (ar dubla H1-ul si
// ar rupe ierarhia de headinguri), dar PASTREAZA subtitlul: acolo scrie ce se
// intampla efectiv (9 conversatii reale cu ChatGPT), iar asta e chiar promisiunea
// care converteste. Varianta implicita ramane neschimbata pentru /scan/<id>.
export default function MiniScanWidget({ variant = 'default', prefill = null }) {
  const isHero = variant === 'hero';
  const { t } = useTranslation();
  const [brandName, setBrandName] = useState('');
  const [loading, setLoading] = useState(false);
  // Faza REALA a asteptarii, nu una simulata:
  //   'classifying' — se stabileste industria (pasul 1);
  //   'preparing'   — se cer concurentii, produsele si cele 9 intrebari;
  //   'scanning'    — cele 9 conversatii au plecat.
  // Nu exista a patra faza vizibila fiindca nu exista niciun semnal de la
  // server intre plecarea celor 9 si verdict.
  const [phase, setPhase] = useState('classifying');
  const [industry, setIndustry] = useState(null);
  const [prepared, setPrepared] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const viewedRef = useRef(false);
  const teaserSeenRef = useRef(false);
  const rootRef = useRef(null);
  // Instrumentarea abandonului: cat a asteptat si in ce faza a plecat.
  // Un ref, nu state — se citeste din handlerul de `pagehide`, care nu are
  // acces la closure-ul ultimului render.
  const waitRef = useRef({ startedAt: 0, phase: null, questions: 0 });


  // ── Impression: o singura data, cand widgetul intra in viewport ──────
  useEffect(() => {
    const el = rootRef.current;
    if (!el || viewedRef.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting) && !viewedRef.current) {
        viewedRef.current = true;
        trackEvent('mini_scan_viewed', {});
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // ── Abandon in timpul asteptarii ─────────────────────────────────────
  // Fara evenimentul asta nu se poate demonstra ca vreo schimbare a ecranului
  // de asteptare a functionat: `mini_scan_result` pleaca doar pe succes, deci
  // exact userii pe care ii pierdem sunt cei care nu apar in nicio cifra.
  // `pagehide` si nu `beforeunload`: al doilea nu se declanseaza pe iOS Safari.
  useEffect(() => {
    if (!loading) return undefined;
    const onLeave = () => {
      const w = waitRef.current;
      if (!w.startedAt) return;
      trackEvent('mini_scan_abandoned', {
        elapsed_ms: Date.now() - w.startedAt,
        phase: w.phase,
        questions_shown: w.questions,
        transport_type: 'beacon',
      });
      w.startedAt = 0;
    };
    window.addEventListener('pagehide', onLeave);
    return () => window.removeEventListener('pagehide', onLeave);
  }, [loading]);

  // ── Teaser-ul (poarta hibrida) a devenit vizibil — o data per widget ──
  useEffect(() => {
    if (!result || teaserSeenRef.current) return;
    teaserSeenRef.current = true;
    trackEvent('mini_scan_teaser_shown', { verdict: result.verdict?.key || 'unknown' });
  }, [result]);

  // Scanul, in trei timpi — ordinea in care userul vede ceva, nu ordinea
  // comoda pentru backend.
  //
  // Pasul 1 (`classify`) stabileste INDUSTRIA si atat. Pentru brandurile pe
  // care le stim deja (registru, o scanare anterioara, tabela `brands`) nu
  // costa niciun apel LLM, deci propozitia de deschidere — „faci parte din
  // industria X si vom investiga vizibilitatea ta in raport cu concurentii" —
  // apare aproape instant, in locul unui ecran gol.
  //
  // Pasul 2 (`prepare`) cere concurentii, produsele si cele 9 intrebari, peste
  // industria deja semnata (`stage_token`). Pasul 3 e scanul, care primeste
  // `prep_token` si nu mai reclasifica — deci intrebarile pe care userul le
  // citeste pe ecran sunt exact cele puse.
  //
  // Un pas 1 sau 2 picat nu opreste nimic: se merge mai departe fara token, iar
  // backendul reface singur ce lipseste. Pasii astia exista pentru afisare, nu
  // pentru masuratoare — nu au voie sa devina puncte de esec intre user si
  // rezultatul lui.
  const runScan = useCallback(async (rawName) => {
    const name = (rawName || '').trim();
    if (name.length < 2) { setError(t('app.mini_scan.error_min_length')); return; }
    setLoading(true); setError(''); setResult(null);
    setIndustry(null); setPrepared(null); setPhase('classifying');
    trackEvent('mini_scan_submitted', { scan_version: 2 });

    const startedAt = Date.now();
    waitRef.current = { startedAt, phase: 'classifying', questions: 0 };

    // ── Pasul 1: industria, primul lucru afisat ──
    let stageToken = null;
    let tClassified = null;
    let industrySource = null;
    try {
      const c = (await acquisitionApi.miniScanClassify(name)).data || {};
      stageToken = c.stage_token || null;
      tClassified = Date.now() - startedAt;
      if (c.ok && (c.categorie_consumator || c.categorie)) {
        industrySource = c.source || null;
        setIndustry({
          categorie: c.categorie_consumator || c.categorie,
          source: industrySource,
        });
      }
    } catch (err) {
      trackEvent('mini_scan_classify_failed', { reason: err.response?.status || 'network' });
    }

    setPhase('preparing');
    waitRef.current.phase = 'preparing';

    // ── Pasul 2: intrebarile, cat mai devreme ──
    let prepToken = null;
    let tPrepared = null;
    try {
      const p = (await acquisitionApi.miniScanPrepare(name, { stage_token: stageToken })).data || {};
      prepToken = p.prep_token || null;
      tPrepared = Date.now() - startedAt;
      if (p.ok && Array.isArray(p.intrebari) && p.intrebari.length > 0) {
        setPrepared(p);
        waitRef.current.questions = p.intrebari.length;
        // Pasul 1 poate sa fi picat, dar pasul 2 intoarce aceeasi industrie:
        // propozitia de deschidere apare mai tarziu, nu deloc.
        if (p.categorie_consumator || p.categorie) {
          setIndustry(prev => prev || {
            categorie: p.categorie_consumator || p.categorie,
            source: p.source || null,
          });
        }
      }
    } catch (err) {
      // Inclusiv 429 pe `prepare`: scanul are propria limita si propriul mesaj.
      trackEvent('mini_scan_prepare_failed', { reason: err.response?.status || 'network' });
    }

    setPhase('scanning');
    waitRef.current.phase = 'scanning';

    // ── Pasul 3: scanul propriu-zis ──
    try {
      const r = await acquisitionApi.miniScan(name, { prep_token: prepToken });
      const d = r.data || {};
      if (d.error) {
        setError(t(`app.mini_scan.error_${d.error}`, t('app.mini_scan.error_generic')));
        trackEvent('mini_scan_failed', { reason: d.error, duration_ms: Date.now() - startedAt });
      } else {
        setResult(d);
        if (d.verdict?.key === 'not_found') {
          trackEvent('mini_scan_not_found', {});
        }
        trackEvent('mini_scan_result', {
          scan_version: 2,
          verdict: d.verdict?.key || 'unknown',
          aparitii: d.aparitii,
          result_completeness: d.responses_ok === d.total_conversatii ? 'full' : 'partial',
          duration_ms: Date.now() - startedAt,
          // Impartirea asteptarii pe fazele reale. E singura cale de a inlocui
          // „tipic 15-30s" (o estimare scrisa intr-un comentariu) cu o
          // distributie masurata, fara sa atingem backendul.
          t_classified_ms: tClassified,
          t_prepared_ms: tPrepared,
          prepared: prepToken ? 'yes' : 'no',
          // De unde a venit industria: fara asta nu se poate spune cati
          // prospecti au avut nevoie de un apel LLM ca sa fie incadrati.
          industry_source: industrySource || 'none',
        });
      }
    } catch (err) {
      const status = err.response?.status;
      setError(status === 429
        ? t('app.mini_scan.error_rate_limit')
        : t('app.mini_scan.error_generic'));
      trackEvent('mini_scan_failed', {
        reason: status === 429 ? 'rate_limit' : 'network',
        duration_ms: Date.now() - startedAt,
      });
    }
    waitRef.current.startedAt = 0;
    setLoading(false);
  }, [t]);

  // ── Prefill din HeroSearch (brand negasit in index → scan direct aici) ──
  //
  // ATENTIE LA POZITIE: effect-ul asta trebuie sa stea DUPA `runScan`.
  // Lista de dependinte se evalueaza la RENDER, iar `runScan` e un `const`
  // din useCallback — referit mai sus in corpul componentei, cade in
  // temporal dead zone si arunca `ReferenceError: Cannot access ... before
  // initialization`, care sparge intreaga pagina prin error boundary.
  // Reprodus in productie pe 2026-08-18.
  // `prefill` e un obiect nou la fiecare cerere, deci effect-ul se
  // redeclanseaza chiar daca numele brandului e acelasi.
  //
  // Scanul PORNESTE singur. Pana acum calea asta doar completa formularul si
  // derula pagina in sus, iar userul — care tocmai cautase brandul si nu-l
  // gasise — trebuia sa mai apese o data un buton, fara sa i se spuna de ce.
  // Clicul acela nu era masurat nicaieri.
  useEffect(() => {
    if (prefill && prefill.name) {
      setBrandName(prefill.name);
      setResult(null);
      setError('');
      runScan(prefill.name);
    }
  }, [prefill, runScan]);

  const handleScan = useCallback((e) => {
    e?.preventDefault();
    runScan(brandName);
  }, [brandName, runScan]);

  // Deblocarea: `result_full` vine de la POST /mini-scan/lead (view server-side).
  // `scan_id`/`shareable` se pastreaza din teaser: view-ul construit din randul
  // DB nu are flagul `shareable` (el descrie insertul, nu scanul — v1 avea
  // aceeasi regula), iar fara el zona de partajare ar disparea dupa deblocare.
  const handleUnlocked = useCallback((full) => {
    setResult(prev => ({
      ...full,
      scan_id: full.scan_id || prev?.scan_id,
      shareable: prev?.shareable ?? full.shareable,
    }));
  }, []);

  // Butonul „alt brand" — numele se goleste DELIBERAT: userul cere explicit
  // altul. Pe calea de EROARE nu se trece pe aici, deci acolo numele ramane in
  // input si „reincearca" nu inseamna „retipareste".
  const reset = () => {
    setResult(null); setError(''); setPrepared(null); setIndustry(null); setBrandName('');
  };

  // ══ REZULTAT (teaser sau, dupa email, analiza completa) ═════════════
  // Totul vine din <MiniScanConsumerResult> — aceeasi componenta ca pagina
  // publica /scan/:scan_id. Poarta de email traieste in ea (e parte din
  // rezultatul v2, pe ambele suprafete); aici raman doar partajarea si
  // deblocarea starii.
  if (result) {
    return (
      <MiniScanConsumerResult
        result={result}
        containerRef={rootRef}
        onReset={reset}
        onUnlocked={handleUnlocked}
        ctaUtmSource="mini_scan"
        ctaEvent="mini_scan_cta_clicked"
        footer={
          /* Partajarea apare DOAR daca backendul confirma ca randul a fost
             salvat (`shareable`). Insertul e best-effort — fara flag am
             oferi un link care da 404 chiar in fata prospectului. */
          <ScanShareBox result={result} source="widget" />
        }
      />
    );
  }

  // ══ FORMULAR ═══════════════════════════════════════════════════════
  // UN singur camp. Detectia de industrie si descriptorul de sector au
  // disparut odata cu pipeline-ul v1: clasificarea e acum dinamica (Prompt 0),
  // deci orice brand, din orice categorie, intra direct.
  return (
    <div ref={rootRef} style={PANEL}>
      <div style={{ textAlign: 'center', marginBottom: 22 }}>
        {!isHero && (
          <>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: 3, color: '#8B6914', textTransform: 'uppercase', marginBottom: 8 }}>
              {t('app.mini_scan.form_eyebrow')}
            </div>
            <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(22px, 6vw, 28px)', fontWeight: 700, color: 'var(--ink, #0A1428)', margin: 0 }}>
              {t('app.mini_scan.form_title')}
            </h2>
          </>
        )}
        <p style={{ fontFamily: BODY, fontSize: 'clamp(15px, 4vw, 17px)', color: 'var(--muted, #6B7280)', marginTop: isHero ? 0 : 10, lineHeight: 1.5 }}>
          {t('app.mini_scan.v2.form_subtitle')}
        </p>
      </div>

      <form onSubmit={handleScan}>
        <input
          type="text"
          placeholder={t('app.mini_scan.input_placeholder')}
          aria-label={t('app.mini_scan.input_placeholder')}
          value={brandName}
          onChange={e => setBrandName(e.target.value)}
          disabled={loading}
          maxLength={100}
          style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
        />

        {error && (
          <div role="alert" style={{ padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, color: '#c0392b', fontSize: 13, marginTop: 14 }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || brandName.trim().length < 2}
          style={{
            ...primaryBtn,
            width: '100%', marginTop: 16, padding: '17px 24px', fontSize: 14,
            background: loading ? '#8a8a8a' : `linear-gradient(135deg, ${GOLD} 0%, #B8972E 100%)`,
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          {loading ? t('app.mini_scan.button_loading') : t('app.mini_scan.button_scan')}
        </button>

        {/* Asteptarea: cele 9 intrebari reale, una cate una, cu timp de citit.
            Inlocuieste tickerul de 4 mesaje care se rotea la 4200ms — un ciclu
            complet dura 16,8s, sub durata tipica a scanului, deci userul vedea
            progresul intorcandu-se la primul mesaj. */}
        {loading && (
          <MiniScanLiveQuestions
            questions={prepared?.intrebari || []}
            categorie={industry?.categorie || null}
            industrySource={industry?.source || null}
            phase={phase}
          />
        )}

        {!loading && (
          <div style={{ textAlign: 'center', marginTop: 14, fontFamily: MONO, fontSize: 10, color: 'var(--muted)', letterSpacing: 1 }}>
            {t('app.mini_scan.v2.form_footnote')}
          </div>
        )}
      </form>
    </div>
  );
}

/**
 * Rezultatul unui mini-scan, randat identic in widget si pe pagina publica
 * /scan/:scan_id. Nu face niciun apel de retea si nu recalculeaza nimic:
 * primeste payload-ul exact cum vine de la backend.
 *
 * Props:
 *  - result: payload-ul intors de POST /mini-scan sau GET /mini-scan/{id}
 *  - containerRef: ref pe panoul exterior (widgetul il foloseste pt impression)
 *  - onReset: daca lipseste, butonul „alt brand" nu se randeaza (pagina publica
 *    nu are unde reseta — acolo CTA-ul duce catre o scanare noua)
 *  - footer: noduri randate intre actiune si CTA (poarta de email, partajare)
 *  - ctaUtmSource / ctaEvent: separa traficul din widget de cel din partajare
 */
export function MiniScanResult({
  result,
  containerRef = null,
  onReset = null,
  footer = null,
  ctaUtmSource = 'mini_scan',
  ctaEvent = 'mini_scan_cta_clicked',
}) {
  const { t } = useTranslation();
  const tone = TONE_COLOR[result.tone] || TONE_COLOR.neutral;
  const partial = result.responses_ok < result.total_responses;
  const quotesState = resolveQuotesState(result);

  // Numerotarea din tabel vine din masuratoarea pe TOT setul, nu din ordinea
  // randurilor afisate: un brand scos din stratul public dispare din tabel, dar
  // nu-i renumerotam pe ceilalti (ar muta pe locul 1 pe cineva care nu e acolo).
  // Nota se randeaza doar cand exista efectiv o discontinuitate vizibila.
  const positionsSkip =
    (result.market_map || []).some((e, i) => e.position != null && e.position !== i + 1);

  return (
    <div ref={containerRef} style={PANEL}>
      {/* ── Antet ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: 2, color: 'var(--muted, #888)', textTransform: 'uppercase', marginBottom: 6 }}>
            {result.industry_label || result.sector_hint || t('app.mini_scan.eyebrow_generic')}
          </div>
          <div style={{ fontFamily: SERIF, fontSize: 'clamp(22px, 6vw, 30px)', fontWeight: 700, color: 'var(--ink, #0A1428)', wordBreak: 'break-word' }}>
            {result.brand_name}
          </div>
        </div>
        {onReset && (
          <button onClick={onReset} style={ghostBtn}>{t('app.mini_scan.another_brand')}</button>
        )}
      </div>

      {/* AI Act art. 50(4). Panoul v1 e legacy, dar linkurile /scan/<id> emise
          inainte de pipeline-ul v2 traiesc mai departe si sunt exact cele cu
          cel mai mult text verbatim neblurat (citate + excerpte mai jos). */}
      <AiContentNotice variant="derived" style={{ marginTop: 0, marginBottom: 18 }} />

      {/* ── Verdict: rata de aparitie, reconstituibila din cifrele de dedesubt ── */}
      <div style={{ padding: 'clamp(16px, 4vw, 24px)', background: `${tone}0F`, border: `1px solid ${tone}33`, borderRadius: 12, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: SERIF, fontSize: 'clamp(38px, 11vw, 60px)', fontWeight: 800, color: tone, lineHeight: 1 }}>
            {result.mentions_count}/{result.total_responses}
          </span>
          <span style={{ fontFamily: MONO, fontSize: 12, color: 'var(--muted)' }}>
            {t('app.mini_scan.mentions_label')}
          </span>
        </div>
        <div style={{ fontFamily: SERIF, fontSize: 'clamp(17px, 4.5vw, 21px)', fontWeight: 700, color: tone, marginTop: 10 }}>
          {t(`app.mini_scan.grade.${result.grade_key}.title`)}
        </div>
        <div style={{ fontFamily: BODY, fontSize: 'clamp(15px, 4vw, 17px)', color: 'var(--ink)', marginTop: 6, lineHeight: 1.55 }}>
          {t(`app.mini_scan.grade.${result.grade_key}.body`)}
        </div>
        {result.avg_rank != null && (
          <div style={{ fontFamily: MONO, fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>
            {t('app.mini_scan.avg_rank', { rank: result.avg_rank, best: result.best_rank })}
          </div>
        )}
        {/* Pozitia relativa, imediat sub cifra absoluta: "6/6" nu inseamna
            nimic pana nu stii ca liderul are tot 6/6. */}
        {result.head_to_head?.you_position != null && result.head_to_head.brands_measured > 1 && (
          <div style={{ fontFamily: MONO, fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
            {t('app.mini_scan.position_line', {
              pos: result.head_to_head.you_position,
              total: result.head_to_head.brands_measured,
            })}
          </div>
        )}
      </div>

      {/* ── Transparenta esantionului: mereu vizibila, nu ascunsa in subsol ── */}
      <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 18 }}>
        {t('app.mini_scan.sample_note', {
          questions: (result.questions || []).length,
          engines: (result.engines_answered || []).join(', ') || '—',
        })}
        {partial && (
          <div style={{ color: '#B7791F', marginTop: 4 }}>
            {t('app.mini_scan.partial_warning', { engines: (result.engines_failed || []).join(', ') })}
          </div>
        )}
      </div>

      {/* ── TABELUL COMPARATIV — piesa centrala ──────────────────────
          Motivul pentru care un brand deschide scanul nu e "apar sau nu",
          ci "unde sunt fata de ei". Randul propriu e mereu prezent, si la
          0 mentiuni: acolo se vede diferenta, nu in absenta lui. Toate
          numerele sunt masurate cu acelasi metru (mini_scan_service:577). */}
      {result.market_map?.length > 0 && (
        <section style={{ marginBottom: 20 }}>
          <SectionTitle>{t('app.mini_scan.compare_title')}</SectionTitle>
          <p style={{ fontFamily: BODY, fontSize: 15, color: 'var(--muted)', margin: '0 0 14px', lineHeight: 1.5 }}>
            {t('app.mini_scan.compare_sub', { total: result.total_responses })}
          </p>

          {result.market_map.map((e) => {
            const pct = result.total_responses ? (e.mentions / result.total_responses) * 100 : 0;
            return (
              <div key={e.name} style={{
                padding: '9px 11px', borderRadius: 8, marginBottom: 5,
                background: e.is_you ? 'rgba(212,175,55,0.14)' : 'transparent',
                border: e.is_you ? `1px solid ${GOLD}` : '1px solid rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--muted)', minWidth: 20 }}>
                    {e.position}.
                  </span>
                  <span style={{
                    fontFamily: SERIF, fontSize: 16, fontWeight: e.is_you ? 800 : 600,
                    color: 'var(--ink)', flex: '1 1 120px', minWidth: 0, wordBreak: 'break-word',
                  }}>
                    {e.name}{e.is_you && ` — ${t('app.mini_scan.you')}`}
                  </span>
                  <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: e.is_you ? '#8B6914' : 'var(--ink)' }}>
                    {e.mentions}/{result.total_responses}
                  </span>
                  {e.best_rank != null && (
                    <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--muted)' }}>
                      {t('app.mini_scan.best_rank_short', { rank: e.best_rank })}
                    </span>
                  )}
                </div>
                {/* Bara e redundanta cu cifra by design: comparatia vizuala
                    se citeste dintr-o privire, cifra ramane pentru precizie. */}
                <div aria-hidden="true" style={{ height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.07)', marginTop: 6 }}>
                  <div style={{
                    width: `${pct}%`, height: '100%', borderRadius: 3,
                    background: e.is_you ? GOLD : 'rgba(10,20,40,0.32)',
                    transition: 'width .5s ease',
                  }} />
                </div>
              </div>
            );
          })}

          {/* Gap-ul, in cuvinte. Un numar langa alt numar cere efort; propozitia nu. */}
          {result.head_to_head?.leader && result.head_to_head.gap_responses > 0 && (
            <div style={{
              marginTop: 12, padding: '11px 13px', borderRadius: 8,
              background: 'rgba(0,0,0,0.03)', borderLeft: `3px solid ${GOLD}`,
              fontFamily: BODY, fontSize: 15.5, color: 'var(--ink)', lineHeight: 1.55,
            }}>
              {t('app.mini_scan.gap_sentence', {
                leader: result.head_to_head.leader.name,
                leader_n: result.head_to_head.leader.mentions,
                you_n: result.mentions_count,
                total: result.total_responses,
                brand: result.brand_name,
              })}
              {result.head_to_head.brands_behind > 0 && ` ${t('app.mini_scan.gap_behind', { n: result.head_to_head.brands_behind })}`}
            </div>
          )}

          {result.visibility_pct === 0 && (
            <div style={{
              marginTop: 8, fontFamily: BODY, fontSize: 15, color: 'var(--ink)', lineHeight: 1.55,
              padding: '0 2px',
            }}>
              {t('app.mini_scan.absent_from_list', { brand: result.brand_name })}
            </div>
          )}

          {positionsSkip && result.head_to_head?.brands_measured > 0 && (
            <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--muted)', marginTop: 10, lineHeight: 1.6 }}>
              {t('app.mini_scan.market_map_position_note', {
                total: result.head_to_head.brands_measured,
              })}
            </div>
          )}

          {/* Cati concurenti declarati ai industriei n-au fost numiti deloc.
              Faptul numarat si interpretarea lui sunt DOUA propozitii separate,
              cu conditii separate: "categoria e nerevendicata" e adevarat doar
              cand nimeni nu domina. Lipite, produceau exact tipul de enunt pe
              care produsul il interzice — textul spunea ca nimeni nu revendica
              piata direct sub un tabel cu un lider prezent in toate raspunsurile. */}
          {result.head_to_head?.registry_absent > 0 && (
            <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--muted)', marginTop: 10, lineHeight: 1.6 }}>
              {t('app.mini_scan.registry_absent', {
                n: result.head_to_head.registry_absent,
                total: result.head_to_head.registry_size,
              })}
              {/* Pragul: liderul sub jumatate din raspunsuri. Peste atat, cineva
                  chiar domina, si a doua propozitie ar contrazice tabelul. */}
              {result.head_to_head.leader != null
                && result.head_to_head.leader.mentions * 2 < result.total_responses && (
                <> {t('app.mini_scan.registry_absent_uncontested', {
                  leader_n: result.head_to_head.leader.mentions,
                  total_resp: result.total_responses,
                })}</>
              )}
            </div>
          )}
        </section>
      )}

      {/* ── Unde stai, in trei cifre ── */}
      {result.head_to_head?.you_position != null && (
        <section style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          <Stat
            value={`#${result.head_to_head.you_position}`}
            label={t('app.mini_scan.stat_position', { total: result.head_to_head.brands_measured })}
          />
          <Stat value={`${result.share_of_voice_pct}%`} label={t('app.mini_scan.stat_sov')} />
          {result.avg_rank != null && (
            <Stat value={`#${result.avg_rank}`} label={t('app.mini_scan.stat_avg_rank')} />
          )}
        </section>
      )}

      {/* ── Ce spune AI-ul, cu propozitia lui, nu cu rezumatul nostru ──
          Copy-ul se alege dupa `quotes_state` (contract backend: "you" |
          "leader_you_present" | "leader_you_absent" | "none"), NU dupa cine e
          subiectul citatelor. Pana la 2026-08-13 orice fallback pe lider randa
          „{{brand}} nu apare in niciun raspuns", inclusiv langa un tabel care
          arata 6/6 — brandul aparea, dar niciodata intr-o fraza citabila.
          Enunt fals langa propria lui dezmintire, permanent pe /scan/<id>. */}
      {quotesState !== 'none' && result.quotes?.length > 0 && (
        <section style={{ marginBottom: 20 }}>
          <SectionTitle>
            {quotesState === 'you'
              ? t('app.mini_scan.quotes_title_you')
              : t('app.mini_scan.quotes_title_leader', { leader: result.quote_about })}
          </SectionTitle>
          {quotesState === 'leader_you_present' && (
            <p style={{ fontFamily: BODY, fontSize: 15, color: 'var(--muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
              {t('app.mini_scan.quotes_sub_leader_present', {
                brand: result.brand_name,
                leader: result.quote_about,
              })}
            </p>
          )}
          {quotesState === 'leader_you_absent' && (
            <p style={{ fontFamily: BODY, fontSize: 15, color: 'var(--muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
              {t('app.mini_scan.quotes_sub_leader_absent', { brand: result.brand_name })}
            </p>
          )}
          {result.quotes.map((q, i) => (
            <blockquote key={i} style={{
              margin: '0 0 8px', padding: '10px 13px', borderRadius: 8,
              background: 'rgba(0,0,0,0.03)', borderLeft: `3px solid ${GOLD}`,
            }}>
              <div style={{ fontFamily: BODY, fontSize: 15.5, color: 'var(--ink)', lineHeight: 1.55 }}>
                „{q.text}"
              </div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--muted)', marginTop: 5 }}>
                {q.engine} · {q.question}
              </div>
            </blockquote>
          ))}
          <AiContentNotice variant="verbatim" />
        </section>
      )}

      {/* ── Pe motor: media ascunde cazul in care unul te stie si altul nu ── */}
      {result.engine_split?.length > 1 && (
        <section style={{ marginBottom: 20 }}>
          <SectionTitle>{t('app.mini_scan.engine_split_title')}</SectionTitle>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {result.engine_split.map((e) => (
              <div key={e.engine} style={{
                flex: '1 1 170px', padding: '10px 12px', borderRadius: 8,
                background: 'rgba(0,0,0,0.03)',
              }}>
                <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--muted)', letterSpacing: 1 }}>
                  {e.engine}
                </div>
                <div style={{ fontFamily: SERIF, fontSize: 19, fontWeight: 700, color: 'var(--ink)', marginTop: 3 }}>
                  {e.you_present}/{e.asked}
                </div>
                <div style={{ fontFamily: BODY, fontSize: 14, color: 'var(--muted)' }}>
                  {e.answered < e.asked
                    ? t('app.mini_scan.engine_partial', { n: e.asked - e.answered })
                    : t('app.mini_scan.engine_you_in')}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Intrebarile reale + ce a raspuns fiecare motor ── */}
      {result.questions?.length > 0 && (
        <section style={{ marginBottom: 20 }}>
          <SectionTitle>{t('app.mini_scan.questions_title')}</SectionTitle>
          {result.questions.map((q, i) => (
            <details key={i} style={{ marginBottom: 8, background: 'rgba(0,0,0,0.02)', borderRadius: 8, padding: '10px 12px' }}>
              <summary style={{ cursor: 'pointer', fontFamily: MONO, fontSize: 12, color: 'var(--ink)', lineHeight: 1.5 }}>
                {q.question}
              </summary>
              {q.answers.map((a, j) => (
                <div key={j} style={{ marginTop: 10, paddingLeft: 10, borderLeft: `2px solid ${a.ok ? GOLD : '#CBD5E0'}` }}>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: a.ok ? GOLD : 'var(--muted)', letterSpacing: 1, marginBottom: 4 }}>
                    {a.llm}
                    {!a.ok && ` · ${t('app.mini_scan.engine_unavailable')}`}
                    {a.ok && a.you_present && ` · ${t('app.mini_scan.you_appear')}${a.you_rank ? ` #${a.you_rank}` : ''}`}
                    {a.ok && !a.you_present && ` · ${t('app.mini_scan.you_absent')}`}
                  </div>
                  {a.ok && a.excerpt && (
                    <div style={{ fontFamily: BODY, fontSize: 14.5, color: 'var(--ink)', lineHeight: 1.55 }}>
                      „{a.excerpt}"
                    </div>
                  )}
                </div>
              ))}
            </details>
          ))}
          <AiContentNotice variant="verbatim" />
        </section>
      )}

      {/* ── Sursele citate efectiv ── */}
      {result.sources?.length > 0 && (
        <section style={{ marginBottom: 20 }}>
          <SectionTitle>{t('app.mini_scan.sources_title')}</SectionTitle>
          <p style={{ fontFamily: BODY, fontSize: 15, color: 'var(--muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
            {t('app.mini_scan.sources_sub')}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {result.sources.map((s) => (
              <span key={s.url} style={{
                fontFamily: MONO, fontSize: 11, padding: '5px 9px', borderRadius: 6,
                background: 'rgba(0,0,0,0.04)', color: 'var(--ink)', wordBreak: 'break-all',
              }}>{s.domain}</span>
            ))}
          </div>
        </section>
      )}

      {/* ── RRI real, doar daca exista rand persistat ── */}
      {result.rri?.rri != null && (
        <section style={{ marginBottom: 20, padding: '12px 14px', borderRadius: 10, background: 'rgba(45,106,79,0.07)' }}>
          <SectionTitle>{t('app.mini_scan.rri_title')}</SectionTitle>
          <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 800, color: '#2D6A4F' }}>
            {Number(result.rri.rri).toFixed(2)}
            {result.rri.rri_tier && (
              <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginLeft: 8 }}>
                {result.rri.rri_tier}
              </span>
            )}
          </div>
          <div style={{ fontFamily: BODY, fontSize: 15, color: 'var(--ink)', marginTop: 4, lineHeight: 1.5 }}>
            {t('app.mini_scan.rri_sub')}
          </div>
        </section>
      )}

      {/* ── O actiune concreta, determinista ── */}
      {result.action?.title && (
        <section style={{ marginBottom: 20 }}>
          <SectionTitle>{t('app.mini_scan.action_title')}</SectionTitle>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(212,175,55,0.07)', borderLeft: `3px solid ${GOLD}` }}>
            <div style={{ fontFamily: SERIF, fontSize: 17, fontWeight: 700, color: 'var(--ink)' }}>
              {result.action.title}
            </div>
            {result.action.why && (
              <div style={{ fontFamily: BODY, fontSize: 15, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
                {result.action.why}
              </div>
            )}
            {result.action.steps?.length > 0 && (
              <ol style={{ fontFamily: BODY, fontSize: 15, color: 'var(--ink)', margin: '8px 0 0', paddingLeft: 20, lineHeight: 1.6 }}>
                {result.action.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            )}
          </div>
        </section>
      )}

      {footer}

      {/* ── CTA: identic pe toate benzile de scor (regula HubSpot) ── */}
      <div style={{ background: 'linear-gradient(135deg, #1a1610 0%, #2a2620 100%)', color: '#FFF', padding: 'clamp(16px, 4vw, 24px)', borderRadius: 12 }}>
        <div style={{ fontFamily: SERIF, fontSize: 'clamp(17px, 4.5vw, 20px)', fontWeight: 700, marginBottom: 10 }}>
          {t('app.mini_scan.cta_title')}
        </div>
        <div style={{ fontFamily: BODY, fontSize: 15, lineHeight: 1.6, marginBottom: 16, opacity: 0.9 }}>
          {t('app.mini_scan.cta_body')}
        </div>
        <Link
          to={registerLinkFromMiniScan(result, ctaUtmSource)}
          onClick={() => trackEvent(ctaEvent, { industry: result.industry || 'unknown' })}
          style={{
            display: 'inline-block', background: GOLD, color: '#0A1428',
            padding: '13px 26px', borderRadius: 10, textDecoration: 'none',
            fontFamily: MONO, fontSize: 12, fontWeight: 700, letterSpacing: 1.4,
          }}
        >
          {t('app.mini_scan.cta_button')}
        </Link>
      </div>
    </div>
  );
}

/**
 * Zona de partajare: linkul permanent catre /scan/<scan_id>, copiere in
 * clipboard si doua intentii de trimitere (WhatsApp, email).
 *
 * Se randeaza DOAR cand `result.shareable === true` — flagul pus de backend
 * dupa ce randul chiar a fost salvat. Insertul e best-effort acolo, deci fara
 * flag am putea oferi un link care da 404.
 *
 * `source` separa in analytics partajarea din widget de cea de pe pagina
 * partajata (al doilea nivel de distributie).
 */
export function ScanShareBox({ result, source = 'widget' }) {
  const { t } = useTranslation();
  const [copyState, setCopyState] = useState('idle');  // idle | ok | fail
  const shownRef = useRef(false);
  const scanId = result?.shareable ? result?.scan_id : null;

  useEffect(() => {
    if (!scanId || shownRef.current) return;
    shownRef.current = true;
    trackEvent('mini_scan_share_shown', { source });
  }, [scanId, source]);

  // Confirmarea de copiere dispare singura — altfel „Link copiat" ramane pe
  // ecran si dupa ce userul a mai facut alte trei lucruri.
  useEffect(() => {
    if (copyState === 'idle') return;
    const id = setTimeout(() => setCopyState('idle'), 5000);
    return () => clearTimeout(id);
  }, [copyState]);

  if (!scanId) return null;

  const url = `${SITE_URL}/scan/${scanId}`;
  // Acelasi mesaj pe ambele ere de payload; doar sursa cifrelor difera
  // (v2: aparitii/total_conversatii, v1: mentions_count/total_responses).
  const isV2 = result?.scan_version === 2;
  const message = t('app.mini_scan.share.message', {
    brand: result.brand_name,
    mentions: isV2 ? (result.aparitii ?? 0) : result.mentions_count,
    total: isV2 ? (result.total_conversatii || 9) : result.total_responses,
  });
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${message} ${url}`)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(
    t('app.mini_scan.share.email_subject', { brand: result.brand_name })
  )}&body=${encodeURIComponent(`${message}\n\n${url}`)}`;

  const handleCopy = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      // Clipboard API lipseste pe origini nesecurizate si in unele webview-uri
      // (in-app browser Facebook/Instagram) — acolo mai merge doar execCommand.
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    setCopyState(ok ? 'ok' : 'fail');
    if (ok) trackEvent('mini_scan_share_clicked', { channel: 'copy', source });
  };

  return (
    <section style={{
      background: 'rgba(212,175,55,0.07)', border: '1px solid rgba(212,175,55,0.25)',
      borderRadius: 12, padding: 'clamp(14px, 4vw, 20px)', marginBottom: 16,
    }}>
      <div style={{ fontFamily: SERIF, fontSize: 'clamp(17px, 4.5vw, 20px)', fontWeight: 700, color: 'var(--ink)' }}>
        {t('app.mini_scan.share.title')}
      </div>
      <div style={{ fontFamily: BODY, fontSize: 15, color: 'var(--muted)', margin: '6px 0 12px', lineHeight: 1.55 }}>
        {t('app.mini_scan.share.body')}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          readOnly
          value={url}
          aria-label={t('app.mini_scan.share.link_aria')}
          onFocus={(e) => e.target.select()}
          style={{ ...inputStyle, flex: '1 1 220px', minWidth: 0, fontSize: 16 }}
        />
        <button
          type="button"
          onClick={handleCopy}
          aria-label={t('app.mini_scan.share.copy_aria')}
          style={{ ...primaryBtn, flex: '0 0 auto' }}
        >
          {copyState === 'ok' ? t('app.mini_scan.share.copied') : t('app.mini_scan.share.copy')}
        </button>
      </div>

      {/* Starea de copiere e anuntata, nu doar colorata — butonul isi schimba
          eticheta, iar cititoarele de ecran primesc textul prin aria-live. */}
      <div aria-live="polite" style={{ minHeight: 18, marginTop: 8, fontFamily: MONO, fontSize: 11, lineHeight: 1.5 }}>
        {copyState === 'ok' && <span style={{ color: '#2D6A4F' }}>{t('app.mini_scan.share.copied_note')}</span>}
        {copyState === 'fail' && <span style={{ color: '#c0392b' }}>{t('app.mini_scan.share.copy_failed')}</span>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('app.mini_scan.share.whatsapp_aria')}
          onClick={() => trackEvent('mini_scan_share_clicked', { channel: 'whatsapp', source })}
          style={shareLinkStyle}
        >
          {t('app.mini_scan.share.whatsapp')}
        </a>
        <a
          href={mailHref}
          aria-label={t('app.mini_scan.share.email_aria')}
          onClick={() => trackEvent('mini_scan_share_clicked', { channel: 'email', source })}
          style={shareLinkStyle}
        >
          {t('app.mini_scan.share.email')}
        </a>
      </div>
    </section>
  );
}

/**
 * Starea blocului de citate, exact cum o declara backendul.
 *
 * Sursa de adevar e `result.quotes_state` (mini_scan_service.py). Derivarea de
 * mai jos e doar pentru payload-urile salvate INAINTE ca acel camp sa existe:
 * aplica aceeasi regula, pe aceleasi campuri ale aceluiasi payload
 * (`quote_about` + `mentions_count`), deci nu introduce nicio informatie noua.
 * Fara ea, un scan vechi redeschis de pe /scan/<id> ar cadea pe „none" si ar
 * pierde citatele care exista in el.
 */
function resolveQuotesState(result) {
  if (result?.quotes_state) return result.quotes_state;
  if (!result?.quotes?.length || !result?.quote_about) return 'none';
  if (result.quote_about === 'you') return 'you';
  return (result.mentions_count || 0) > 0 ? 'leader_you_present' : 'leader_you_absent';
}

function Stat({ value, label }) {
  return (
    <div style={{
      flex: '1 1 120px', padding: '10px 12px', borderRadius: 8,
      background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.22)',
    }}>
      <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 800, color: 'var(--ink)', lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
        {label}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
      {children}
    </div>
  );
}

// fontSize 16 peste tot: sub 16px Safari iOS face auto-zoom la focus, iar
// vechiul formular avea input 16 si select 15 — deci se comportau diferit.
const inputStyle = {
  padding: '14px 16px',
  fontSize: 16,
  fontFamily: MONO,
  border: '2px solid rgba(212,175,55,0.3)',
  borderRadius: 10,
  outline: 'none',
  background: 'var(--cream, #FAFAF7)',
  color: 'var(--ink, #0A1428)',
};

const primaryBtn = {
  padding: '14px 22px',
  background: `linear-gradient(135deg, ${GOLD} 0%, #B8972E 100%)`,
  color: '#0A1428',
  border: 'none',
  borderRadius: 10,
  fontFamily: MONO,
  fontSize: 12.5,
  fontWeight: 700,
  letterSpacing: 1.4,
  cursor: 'pointer',
  textTransform: 'uppercase',
};

const ghostBtn = {
  background: 'transparent',
  border: '1px solid rgba(0,0,0,0.12)',
  padding: '8px 13px',
  borderRadius: 8,
  fontFamily: MONO,
  fontSize: 11,
  cursor: 'pointer',
  color: 'var(--muted)',
  flex: '0 0 auto',
};

const shareLinkStyle = {
  display: 'inline-block',
  padding: '10px 16px',
  borderRadius: 8,
  border: '1px solid rgba(0,0,0,0.12)',
  background: 'var(--surface, #FFFFFF)',
  color: 'var(--ink, #0A1428)',
  textDecoration: 'none',
  fontFamily: MONO,
  fontSize: 11.5,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
};
